import {
    createArrayField,
    createObjectField,
    createPrimitiveField,
    createUnionField,
    type InferFieldValue,
    literal,
    type ValidationError,
} from '@adv-frontend/form-kit';
import { MEDIA_TYPE_VIDEO } from './constants/videoStatuses';
import { type OpenApiSchemaVideo } from '../../../api/schema';
import { pluralize } from '../../string';
import { factoryGetErrorMessage } from './factoryGetErrorMessage';
import { createMediaMetadataField } from './mediaMetadata';

export const IMAGE_IS_REQUIRED_FOR_VIDEO_ERROR = 'IMAGE_IS_REQUIRED_FOR_VIDEO_ERROR';

const MIN_COUNT_IMAGES_ERROR_CODE = 'MIN_COUNT_IMAGES_ERROR';

type MediaMinCountImagesParams = {
    minCountImages: number;
};

type MediaV2FieldValidationError =
    | ValidationError<typeof MIN_COUNT_IMAGES_ERROR_CODE, MediaMinCountImagesParams>
    | ValidationError<typeof IMAGE_IS_REQUIRED_FOR_VIDEO_ERROR, MediaMinCountImagesParams>;

export const createMediaV2Field = (minCountImages: number = 0) =>
    createArrayField({
        field: createSingleMediaV2Field(),
        validators: (createValidators) =>
            createValidators<MediaV2FieldValidationError>({
                onChange: [
                    (value, fieldApi) => {
                        if (value.length < minCountImages) {
                            fieldApi.addValidationError({
                                code: MIN_COUNT_IMAGES_ERROR_CODE,
                                params: { minCountImages },
                            });
                        }
                    },
                    (value, fieldApi) => {
                        if (
                            value.filter((item) => item.type === MEDIA_TYPE_VIDEO).length > 0 &&
                            value.filter((item) => item.type === 'IMAGE').length < 1
                        ) {
                            fieldApi.addValidationError({
                                code: IMAGE_IS_REQUIRED_FOR_VIDEO_ERROR,
                                params: { minCountImages },
                            });
                        }
                    },
                ],
            }),
    });

const mediaV2FieldGetErrorMessage = factoryGetErrorMessage<MediaV2FieldValidationError>({
    [MIN_COUNT_IMAGES_ERROR_CODE]: ({ minCountImages }) =>
        `Необходимо загрузить минимум ${pluralize(minCountImages, '%d файл', '%d файла', '%d файлов')}`,
    [IMAGE_IS_REQUIRED_FOR_VIDEO_ERROR]: 'Добавьте хотя бы одно изображение товара в дополнение к видео',
});

export const getMediaV2FieldErrorMessage = (errors: MediaV2FieldValidationError[]): string | undefined => {
    const error = errors[0];

    return error ? mediaV2FieldGetErrorMessage(error) : undefined;
};

export const createImageField = () =>
    createObjectField({
        fields: {
            id: createPrimitiveField({ defaultValue: '' }),
            display_sequence: createPrimitiveField({ defaultValue: 0 }),
            file_id: createPrimitiveField({ defaultValue: '' }),
            file_path: createPrimitiveField({ defaultValue: '' }),
            type: createPrimitiveField({
                defaultValue: literal<'IMAGE' | 'OTHER'>('IMAGE'),
            }),
            sizes: createArrayField({
                field: createObjectField({
                    fields: {
                        alias: createPrimitiveField({ defaultValue: '' }),
                        width: createPrimitiveField({ defaultValue: 0 }),
                    },
                }),
            }),
            metadata: createMediaMetadataField(),
        },
    });

export const createVideoField = () =>
    createObjectField({
        fields: {
            id: createPrimitiveField({ defaultValue: '' }),
            display_sequence: createPrimitiveField({ defaultValue: 0 }),
            type: createPrimitiveField({
                defaultValue: literal<'VIDEO'>(MEDIA_TYPE_VIDEO),
            }),
            video_id: createPrimitiveField({ defaultValue: '' }),
            first_frame_url: createPrimitiveField({ defaultValue: '' }),
            stream_dash_url: createPrimitiveField({ defaultValue: '' }),
            stream_hls_url: createPrimitiveField({ defaultValue: '' }),
            title: createPrimitiveField({ defaultValue: '' }),
            status: createPrimitiveField({ defaultValue: literal<OpenApiSchemaVideo['status']>('processing') }),
        },
    });

export const createSingleMediaV2Field = () => {
    const imageField = createImageField();
    const videoField = createVideoField();

    return createUnionField({
        key: 'type',
        fields: [imageField, videoField],
        defaultValue: imageField.defaultValue,
    });
};

export const isImageMediaV2Field = (
    field: MediaV2Field
): field is InferFieldValue<ReturnType<typeof createImageField>> => field.type === 'IMAGE';

export const mediaV2Field = createMediaV2Field(1);
export type MediaV2Field = InferFieldValue<(typeof mediaV2Field)['field']>;

export const videoField = createVideoField();
export type VideoField = InferFieldValue<typeof videoField>;
